<template>
  <el-config-provider :locale="elementPlusLocale">
    <router-view />

    <!-- 全局回到顶部：页面下滑超过一屏后出现，点击丝滑滚回顶部 -->
    <transition name="back-top-fade">
      <button v-show="showBackTop && !isPopupWindow && route.path !== '/get-combined'" type="button" class="global-back-top"
        :aria-label="t('button.back_to_top')" @click="scrollToTop">
        <el-icon>
          <ArrowUpBold />
        </el-icon>
      </button>
    </transition>
  </el-config-provider>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watchEffect } from 'vue'
import { ArrowUpBold } from '@element-plus/icons-vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { ElConfigProvider } from 'element-plus'
import ko from 'element-plus/es/locale/lang/ko'
import zhCn from 'element-plus/es/locale/lang/zh-cn'

const { t, locale } = useI18n()
const route = useRoute()
const isPopupWindow = window.name === 'chinaone-popup'
watchEffect(() => {
  document.documentElement.classList.toggle('chinaone-popup-window', isPopupWindow || route.path === '/get-combined')
})
const elementPlusLocale = computed(() => (
  String(locale.value || '').toLowerCase().startsWith('ko') ? ko : zhCn
))
const showBackTop = ref(false)
let scrollTickId = 0
let scrollAnimId = 0
let paginationSizeOwner = null

const scrollKeys = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '])

const onScroll = () => {
  if (scrollTickId) return
  scrollTickId = requestAnimationFrame(() => {
    showBackTop.value = window.scrollY > 300
    scrollTickId = 0
  })
}

// easeOutCubic：点击后立即起步，接近顶部时平滑减速
const scrollToTop = () => {
  stopScrollAnim()
  const start = window.scrollY
  if (start <= 0) return
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo(0, 0)
    return
  }
  const duration = Math.min(220 + start * 0.18, 650)
  const startTime = performance.now()
  const ease = (p) => 1 - Math.pow(1 - p, 3)
  const step = (now) => {
    const p = Math.min((now - startTime) / duration, 1)
    window.scrollTo(0, start * (1 - ease(p)))
    if (p < 1) {
      scrollAnimId = requestAnimationFrame(step)
    } else {
      scrollAnimId = 0
    }
  }
  scrollAnimId = requestAnimationFrame(step)
}

// 回顶动画期间用户手动滚动（滚轮/触摸）则立即交还控制权
const stopScrollAnim = () => {
  if (scrollAnimId) {
    cancelAnimationFrame(scrollAnimId)
    scrollAnimId = 0
  }
}

const stopScrollAnimByKey = (event) => {
  if (scrollKeys.has(event.key)) stopScrollAnim()
}

const isVisibleElement = (element) => {
  if (!(element instanceof HTMLElement)) return false
  const rect = element.getBoundingClientRect()
  const style = window.getComputedStyle(element)
  return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden'
}

const findPreviousVisibleSibling = (element) => {
  let sibling = element?.previousElementSibling
  while (sibling) {
    if (isVisibleElement(sibling)) return sibling
    sibling = sibling.previousElementSibling
  }
  return null
}

// 从分页向上寻找同一列表中的表格或列表容器，避免滚回整个页面顶部。
const findPaginationScrollTarget = (pagination) => {
  const paginationTop = pagination.getBoundingClientRect().top
  let explicitScope = pagination.parentElement
  while (explicitScope && explicitScope !== document.body) {
    const explicitTargets = Array.from(
      explicitScope.querySelectorAll('[data-pagination-scroll-target]')
    ).filter((candidate) => (
      isVisibleElement(candidate) && candidate.getBoundingClientRect().top < paginationTop - 4
    ))
    if (explicitTargets.length) {
      return explicitTargets.reduce((nearest, candidate) => {
        if (!nearest) return candidate
        return candidate.getBoundingClientRect().top > nearest.getBoundingClientRect().top
          ? candidate
          : nearest
      }, null)
    }
    explicitScope = explicitScope.parentElement
  }

  let branch = pagination
  let scope = pagination.parentElement

  while (scope && scope !== document.body) {
    const targetSelectors = [
      '.el-table',
      '.mobile-payment-list',
      '.mobile-record-list',
      'table',
    ]
    for (const selector of targetSelectors) {
      const visibleCandidates = Array.from(scope.querySelectorAll(selector)).filter((candidate) => {
        if (pagination.contains(candidate) || !isVisibleElement(candidate)) return false
        return candidate.getBoundingClientRect().top < paginationTop - 4
      })
      if (visibleCandidates.length) {
        return visibleCandidates.reduce((nearest, candidate) => {
          if (!nearest) return candidate
          return candidate.getBoundingClientRect().top > nearest.getBoundingClientRect().top
            ? candidate
            : nearest
        }, null)
      }
    }

    const previousSibling = findPreviousVisibleSibling(branch)
    if (previousSibling) return previousSibling

    branch = scope
    scope = scope.parentElement
  }

  return null
}

const findScrollableParent = (element) => {
  let parent = element?.parentElement
  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent)
    const canScroll = /(auto|scroll)/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight
    if (canScroll) return parent
    parent = parent.parentElement
  }
  return null
}

const scrollPaginationListIntoView = (pagination) => {
  const target = findPaginationScrollTarget(pagination)
  if (!target) return

  const dialog = pagination.closest('.el-dialog')
  if (dialog) {
    const scrollParent = findScrollableParent(target)
    if (!scrollParent || !dialog.contains(scrollParent)) return
    const targetTop = target.getBoundingClientRect().top
    const parentTop = scrollParent.getBoundingClientRect().top
    scrollParent.scrollTo({
      top: Math.max(0, scrollParent.scrollTop + targetTop - parentTop - 12),
      behavior: 'auto',
    })
    return
  }

  const topOffset = window.innerWidth <= 768 ? 72 : 96
  const targetTop = target.getBoundingClientRect().top
  if (targetTop >= topOffset && targetTop <= window.innerHeight * 0.4) return
  window.scrollTo({
    top: Math.max(0, window.scrollY + targetTop - topOffset),
    behavior: 'auto',
  })
}

const schedulePaginationScroll = (pagination) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => scrollPaginationListIntoView(pagination))
  })
}

const onPaginationPointerDown = (event) => {
  const target = event.target instanceof Element ? event.target : null
  const pageSizeSelect = target?.closest('.el-pagination .el-select')
  if (pageSizeSelect) {
    paginationSizeOwner = pageSizeSelect.closest('.el-pagination')
    return
  }
  if (!target?.closest('.el-select-dropdown')) paginationSizeOwner = null
}

const onPaginationClick = (event) => {
  const target = event.target instanceof Element ? event.target : null
  if (!target) return

  const pageControl = target.closest('.el-pager li, .el-pagination .btn-prev, .el-pagination .btn-next')
  if (pageControl) {
    if (pageControl.matches('.is-active, :disabled')) return
    const pagination = pageControl.closest('.el-pagination')
    if (pagination) schedulePaginationScroll(pagination)
    return
  }

  if (paginationSizeOwner && target.closest('.el-select-dropdown__item')) {
    schedulePaginationScroll(paginationSizeOwner)
    paginationSizeOwner = null
  }
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('wheel', stopScrollAnim, { passive: true })
  window.addEventListener('touchmove', stopScrollAnim, { passive: true })
  window.addEventListener('pointerdown', stopScrollAnim, { passive: true })
  window.addEventListener('keydown', stopScrollAnimByKey)
  document.addEventListener('pointerdown', onPaginationPointerDown, { passive: true })
  document.addEventListener('click', onPaginationClick)
  onScroll()
})
onUnmounted(() => {
  document.documentElement.classList.remove('chinaone-popup-window')
  window.removeEventListener('scroll', onScroll)
  window.removeEventListener('wheel', stopScrollAnim)
  window.removeEventListener('touchmove', stopScrollAnim)
  window.removeEventListener('pointerdown', stopScrollAnim)
  window.removeEventListener('keydown', stopScrollAnimByKey)
  document.removeEventListener('pointerdown', onPaginationPointerDown)
  document.removeEventListener('click', onPaginationClick)
  if (scrollTickId) cancelAnimationFrame(scrollTickId)
  stopScrollAnim()
})
</script>

<style scoped>
.global-back-top {
  position: fixed;
  right: 30px;
  bottom: 46px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: #df2f35;
  color: #fff;
  box-shadow: 0 4px 14px rgba(223, 47, 53, .35);
  cursor: pointer;
  transition: background-color .2s ease, transform .2s ease;
}

.global-back-top:hover {
  background: #c8262c;
  transform: translateY(-2px);
}

.global-back-top .el-icon {
  font-size: 22px;
}

.back-top-fade-enter-active,
.back-top-fade-leave-active {
  transition: opacity .25s ease, transform .25s ease;
}

.back-top-fade-enter-from,
.back-top-fade-leave-to {
  opacity: 0;
  transform: translateY(14px);
}

@media (max-width: 991px) {
  .global-back-top {
    right: 16px;
    bottom: calc(30px + env(safe-area-inset-bottom));
    width: 40px;
    height: 40px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .global-back-top,
  .back-top-fade-enter-active,
  .back-top-fade-leave-active {
    transition: none;
  }
}
</style>

<style>
/* 独立业务弹窗及页面内对话框统一复选框尺寸。 */
html.chinaone-popup-window .el-checkbox__inner,
.el-dialog .el-checkbox__inner,
.el-drawer .el-checkbox__inner,
.el-message-box .el-checkbox__inner {
  width: 16px !important;
  height: 16px !important;
}

html.chinaone-popup-window input[type="checkbox"]:not(.el-checkbox__original),
.el-dialog input[type="checkbox"]:not(.el-checkbox__original),
.el-drawer input[type="checkbox"]:not(.el-checkbox__original),
.el-message-box input[type="checkbox"]:not(.el-checkbox__original) {
  width: 16px !important;
  height: 16px !important;
}
</style>
